import { useTenantRoles } from "@/api/tenant.api";
import { useUpdateUserRole, useUser } from "@/api/user.api";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogDescription,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { useTenantStore } from "@/store/tenant/tenant.store";
import { t } from "i18next";
import isNil from "lodash.isnil";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

export const UserEdit = () => {
    const { userId } = useParams();
    const selectedTenant = useTenantStore((state) => state.selectedTenant);
    const { data: user } = useUser(userId ? parseInt(userId) : undefined);
    const navigate = useNavigate();

    const { data: roles = [] } = useTenantRoles(selectedTenant?.id);
    const [selectedRoleId, setSelectedRoleId] = useState<string>("none");
    const { mutate: updateUserRole } = useUpdateUserRole();

    const handleUpdateUserRole = () => {
        updateUserRole({
            tenantId: selectedTenant?.id ?? -1,
            userId: user?.id ?? -1,
            roleId: selectedRoleId === "none" ? null : parseInt(selectedRoleId),
        });

        navigate("..");
    };

    useEffect(() => {
        if (user) {
            setSelectedRoleId(user?.tenants?.[0]?.role?.id.toString() ?? "none");
        }
    }, [user]);

    return (
        <Dialog open={!isNil(user)} onOpenChange={() => navigate("..")}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="mb-4">{t("users.edit")}</DialogTitle>
                    <DialogDescription>
                        <div className="space-y-1">
                            <div>
                                <strong>{user?.name || user?.email}</strong>
                            </div>
                            {user?.name && (
                                <div className="text-sm text-muted-foreground">{user?.email}</div>
                            )}
                        </div>
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <label className="text-sm font-medium">{t("users.roles.title")}</label>
                        <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
                            <SelectTrigger className="mt-1">
                                <SelectValue placeholder={t("users.roles.title")} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">{t("users.roles.none")}</SelectItem>
                                {roles.map((role) => (
                                    <SelectItem key={role.id} value={role.id.toString()}>
                                        {role.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => navigate("..")}>
                        {t("general.cancel")}
                    </Button>
                    <Button onClick={handleUpdateUserRole}>{t("general.save")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
